/**
 * @fileoverview Privacy Policy page for Onicorn platform
 * @module routes/privacy
 */

import type { ReactElement } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';

/**
 * Privacy Policy page component.
 *
 * Displays the complete Privacy Policy for the Onicorn platform,
 * covering data collection, blockchain transparency, third-party services,
 * user rights, and data protection practices.
 *
 * @component
 */
function PrivacyPage(): ReactElement {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-6 pt-28 pb-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#B3B3B3] hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <h1 className="text-3xl md:text-4xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-[#B3B3B3] mb-8">Last updated: January 2026</p>

        {/* Content */}
        <div className="prose prose-invert max-w-none space-y-8">
          {/* Introduction */}
          <section>
            <p className="text-[#B3B3B3] leading-relaxed">
              This Privacy Policy describes how Onicorn ("we," "us," or "our") collects, uses, and protects your
              information when you use our Web3 fundraising platform ("Platform"). By using the Platform, you agree
              to the collection and use of information in accordance with this policy.
            </p>
          </section>

          {/* Section 1 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">1. Information We Collect</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              We collect different types of information to provide and improve our services:
            </p>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">1.1 Blockchain Data</h3>
            <p className="text-[#B3B3B3] leading-relaxed">
              When you interact with the Platform through your cryptocurrency wallet, we may access publicly available
              blockchain data, including:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>Wallet addresses you connect to the Platform</li>
              <li>Transaction history and investment records</li>
              <li>Token balances and holdings</li>
              <li>Smart contract interactions</li>
            </ul>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              This information is inherently public on the blockchain and is not collected by us but accessed from
              public ledgers.
            </p>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">1.2 Automatically Collected Information</h3>
            <p className="text-[#B3B3B3] leading-relaxed">
              When you access the Platform, we automatically collect certain information, including:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>Device information (browser type, operating system, device identifiers)</li>
              <li>IP address and approximate location</li>
              <li>Usage data (pages visited, features used, time spent)</li>
              <li>Referral sources and navigation paths</li>
              <li>Error logs and performance data</li>
            </ul>

            <h3 className="text-lg font-semibold text-white mt-6 mb-3">1.3 Voluntarily Provided Information</h3>
            <p className="text-[#B3B3B3] leading-relaxed">
              You may choose to provide additional information when using certain features:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>Email address (for notifications or account recovery)</li>
              <li>Social media handles (for KOL verification or profile linking)</li>
              <li>Communication preferences</li>
              <li>KYC documentation (if required by regulation)</li>
              <li>Project submission information (for project owners)</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">2. How We Use Your Information</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              We use the collected information for the following purposes:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>To provide, maintain, and improve the Platform</li>
              <li>To process and display your investments and transaction history</li>
              <li>To verify KOL identities and track referral commissions</li>
              <li>To send notifications about your investments, vesting schedules, and Platform updates</li>
              <li>To detect and prevent fraud, abuse, and security threats</li>
              <li>To comply with legal obligations and regulatory requirements</li>
              <li>To analyze usage patterns and improve user experience</li>
              <li>To communicate with you about support requests or inquiries</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">3. Information Sharing</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              We do not sell your personal information. We may share your information in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li><strong className="text-white">Service Providers:</strong> With third-party vendors who assist in operating the Platform (hosting, analytics, security)</li>
              <li><strong className="text-white">Legal Requirements:</strong> When required by law, legal process, or government request</li>
              <li><strong className="text-white">Protection:</strong> To protect our rights, privacy, safety, or property, and that of our users</li>
              <li><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong className="text-white">With Your Consent:</strong> When you explicitly authorize us to share your information</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">4. Blockchain Transparency</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              <strong className="text-white">Important:</strong> Transactions on blockchain networks are inherently public
              and transparent. When you make investments or interact with smart contracts through the Platform:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>Your wallet address and transaction details are recorded on the public blockchain</li>
              <li>This information cannot be deleted or modified due to blockchain's immutable nature</li>
              <li>Anyone can view transaction history associated with your wallet address</li>
              <li>We cannot control or remove data stored on public blockchain networks</li>
            </ul>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              By using the Platform, you acknowledge and accept the public nature of blockchain transactions.
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">5. Third-Party Services</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              The Platform integrates with various third-party services that have their own privacy policies:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li><strong className="text-white">Wallet Providers:</strong> MetaMask, WalletConnect, and other wallet services</li>
              <li><strong className="text-white">Blockchain Networks:</strong> BNB Smart Chain and other supported networks</li>
              <li><strong className="text-white">Authentication Providers:</strong> Google, Twitter/X, and other OAuth providers</li>
              <li><strong className="text-white">Analytics Services:</strong> Services that help us understand Platform usage</li>
            </ul>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              We encourage you to review the privacy policies of these third-party services. We are not responsible
              for their privacy practices.
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">6. Data Retention</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              We retain your information for as long as necessary to:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>Provide the Platform services to you</li>
              <li>Comply with legal, accounting, or reporting requirements</li>
              <li>Resolve disputes and enforce our agreements</li>
              <li>Maintain records for regulatory compliance</li>
            </ul>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              Off-chain data may be deleted upon request, subject to legal retention requirements. On-chain data
              stored on public blockchains cannot be deleted.
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">7. Data Security</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              We implement appropriate technical and organizational measures to protect your information, including:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and audits</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Monitoring for suspicious activities</li>
            </ul>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              However, no method of transmission over the Internet or electronic storage is 100% secure. We cannot
              guarantee absolute security of your information.
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">8. Your Rights and Choices</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              Depending on your jurisdiction, you may have certain rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li><strong className="text-white">Access:</strong> Request access to the personal information we hold about you</li>
              <li><strong className="text-white">Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong className="text-white">Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
              <li><strong className="text-white">Portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong className="text-white">Opt-out:</strong> Opt out of marketing communications and certain data processing</li>
              <li><strong className="text-white">Withdraw Consent:</strong> Withdraw previously given consent for data processing</li>
            </ul>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              To exercise these rights, please contact us at privacy@onicorn.io. Note that blockchain data cannot
              be modified or deleted due to its immutable nature.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">9. International Data Transfers</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              Your information may be transferred to and processed in countries other than your country of residence.
              These countries may have different data protection laws. By using the Platform, you consent to the
              transfer of your information to these countries.
            </p>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              We take steps to ensure that your information receives adequate protection in accordance with this
              Privacy Policy and applicable data protection laws.
            </p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">10. Children's Privacy</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              The Platform is not intended for individuals under the age of 18 (or the age of majority in your
              jurisdiction). We do not knowingly collect personal information from children. If you believe we
              have collected information from a child, please contact us immediately.
            </p>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">11. Cookies and Tracking Technologies</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              We use cookies and similar tracking technologies to:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>Remember your preferences and settings</li>
              <li>Authenticate your wallet connection</li>
              <li>Analyze Platform usage and performance</li>
              <li>Improve user experience</li>
            </ul>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              You can manage cookie preferences through your browser settings. Disabling cookies may affect
              Platform functionality.
            </p>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">12. Changes to This Policy</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of material changes by:
            </p>
            <ul className="list-disc list-inside text-[#B3B3B3] mt-4 space-y-2">
              <li>Posting the updated policy on the Platform</li>
              <li>Updating the "Last updated" date at the top of this page</li>
              <li>Sending notifications through the Platform or email (for significant changes)</li>
            </ul>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              Your continued use of the Platform after any changes indicates your acceptance of the updated
              Privacy Policy.
            </p>
          </section>

          {/* Section 13 */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">13. Contact Us</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices,
              please contact us at:
            </p>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              <strong className="text-white">Onicorn</strong><br />
              Email: privacy@onicorn.io<br />
              Website: https://onicorn.io
            </p>
            <p className="text-[#B3B3B3] leading-relaxed mt-4">
              For data protection inquiries or to exercise your rights, please email privacy@onicorn.io with
              "Data Protection Request" in the subject line.
            </p>
          </section>

          {/* Related Links */}
          <section>
            <h2 className="text-xl font-bold text-white mb-4">Related Documents</h2>
            <p className="text-[#B3B3B3] leading-relaxed">
              Please also review our{' '}
              <Link to="/terms" className="text-purple-400 hover:text-purple-300 underline">
                Terms of Service
              </Link>
              {' '}for additional information about using the Onicorn platform.
            </p>
          </section>

          {/* Footer */}
          <div className="border-t border-[#282828] pt-8 mt-12">
            <p className="text-[#666] text-sm">
              By using the Onicorn platform, you acknowledge that you have read and understood this Privacy Policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/privacy')({
  component: PrivacyPage,
});
